/**
 * Singleton to control the states of guides for each surface
 */

const guidesMap = {};
const SNAP_THRESHOLD = 20; // pixels in screen space

export function useElementPositionGuides({surface}) {
  const surfaceEditorUtils = useSurfaceEditorUtils();

  const guides = guidesMap[surface.id]
    || (guidesMap[surface.id] = {
      xAlignments: ref({}),
      yAlignments: ref({})
    });

  return {
    setGuides({element, surface, dragHandle, positionGuidesEnabled, alignMiddle = true}) {
      // If guides disabled, clear and return no snap
      if (!positionGuidesEnabled.value) {
        guidesMap[surface.id].xAlignments.value = {};
        guidesMap[surface.id].yAlignments.value = {};
        return {snapDx: 0, snapDy: 0};
      }

      // Build reference lines from other elements and surface edges
      const xRefLines = []; // {value: x-coord, top, bottom, isSurface}
      const yRefLines = []; // {value: y-coord, left, right, isSurface}

      // Add surface reference lines
      xRefLines.push(
        {value: 0, top: 0, bottom: surface.height, isSurface: true},
        {value: surface.width, top: 0, bottom: surface.height, isSurface: true},
        {value: surface.width / 2, top: 0, bottom: surface.height, isSurface: true}
      );
      yRefLines.push(
        {value: 0, left: 0, right: surface.width, isSurface: true},
        {value: surface.height, left: 0, right: surface.width, isSurface: true},
        {value: surface.height / 2, left: 0, right: surface.width, isSurface: true}
      );

      // Add reference lines from other elements
      surface.elements.forEach(otherElement => {
        // Skip the dragged element and background
        if (otherElement.id === element.id) {
          return;
        }

        let bounds;
        if (otherElement.rotation && otherElement.rotation !== 0) {
          // Use AABB for rotated elements
          bounds = surfaceEditorUtils.getRotatedBoundingBox({
            x: otherElement.x,
            y: otherElement.y,
            width: otherElement.width,
            height: otherElement.height,
            rotation: otherElement.rotation
          });
        } else {
          // Use raw bounds for non-rotated elements
          bounds = {
            x: otherElement.x,
            y: otherElement.y,
            width: otherElement.width,
            height: otherElement.height
          };
        }

        const left = bounds.x;
        const right = bounds.x + bounds.width;
        const top = bounds.y;
        const bottom = bounds.y + bounds.height;
        const middleX = bounds.x + bounds.width / 2;
        const middleY = bounds.y + bounds.height / 2;

        // Add x reference lines (vertical guides)
        xRefLines.push(
          {value: left, top, bottom, isSurface: false},
          {value: right, top, bottom, isSurface: false}
        );
        if (alignMiddle) {
          xRefLines.push({value: middleX, top, bottom, isSurface: false});
        }

        // Add y reference lines (horizontal guides)
        yRefLines.push(
          {value: top, left, right, isSurface: false},
          {value: bottom, left, right, isSurface: false}
        );
        if (alignMiddle) {
          yRefLines.push({value: middleY, left, right, isSurface: false});
        }
      });

      // Compute dragged element's effective bounds
      let draggedBounds;
      if (element.rotation && element.rotation !== 0) {
        draggedBounds = surfaceEditorUtils.getRotatedBoundingBox({
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
          rotation: element.rotation
        });
      } else {
        draggedBounds = {
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height
        };
      }

      const draggedEdges = {
        left: draggedBounds.x,
        right: draggedBounds.x + draggedBounds.width,
        top: draggedBounds.y,
        bottom: draggedBounds.y + draggedBounds.height,
        middleX: draggedBounds.x + draggedBounds.width / 2,
        middleY: draggedBounds.y + draggedBounds.height / 2
      };

      // Determine which edges to check based on drag handle and rotation
      let xEdgesToCheck = [];
      let yEdgesToCheck = [];

      const isRotated = element.rotation && element.rotation !== 0;
      const isMove = dragHandle === SURFACE_EDITOR_DRAG_HANDLE.all;

      // Check all edges, optionally including middle for alignment guides
      xEdgesToCheck = alignMiddle ? ['left', 'right', 'middleX'] : ['left', 'right'];
      yEdgesToCheck = alignMiddle ? ['top', 'bottom', 'middleY'] : ['top', 'bottom'];

      // Find all aligned edges and closest snap per axis
      let snapDx = 0;
      let snapDy = 0;
      const allAlignedXPositions = new Map(); // Map<xValue, {edges: Set, refLines: []}>
      const allAlignedYPositions = new Map(); // Map<yValue, {edges: Set, refLines: []}>

      // X-axis: find all edges within threshold and track closest for snapping
      // Prioritize middle alignments over edge alignments
      let minXDistance = SNAP_THRESHOLD + 1;
      let bestXEdge = null;
      let bestXRef = null;

      xEdgesToCheck.forEach(edgeName => {
        const edgeValue = draggedEdges[edgeName];
        xRefLines.forEach(refLine => {
          const distance = Math.abs(edgeValue - refLine.value);

          // Track if this is the closest for snapping
          // Prioritize middle edge: if middle is within threshold, prefer it over edge alignments
          const isMiddle = edgeName === 'middleX';
          const isBetterMatch = distance < minXDistance || (isMiddle && distance <= SNAP_THRESHOLD && !bestXEdge?.includes('middle'));

          if (isBetterMatch) {
            minXDistance = distance;
            bestXEdge = edgeName;
            bestXRef = refLine;
          }

          // Track all alignments within threshold
          if (distance <= SNAP_THRESHOLD) {
            const key = refLine.value;
            if (!allAlignedXPositions.has(key)) {
              allAlignedXPositions.set(key, {edges: new Set(), refLines: []});
            }
            const alignment = allAlignedXPositions.get(key);
            alignment.edges.add(edgeName);
            if (!alignment.refLines.some(r => r === refLine)) {
              alignment.refLines.push(refLine);
            }
          }
        });
      });

      // Apply snap for the closest edge
      if (minXDistance <= SNAP_THRESHOLD && bestXRef) {
        snapDx = bestXRef.value - draggedEdges[bestXEdge];

        // During resize operations, middle position changes at half the rate of edge movement
        // Scale the snap delta by 2x to compensate (only for handles that affect width)
        if (!isMove && bestXEdge === 'middleX') {
          const affectsWidth = [
            SURFACE_EDITOR_DRAG_HANDLE.left,
            SURFACE_EDITOR_DRAG_HANDLE.right,
            SURFACE_EDITOR_DRAG_HANDLE.topLeft,
            SURFACE_EDITOR_DRAG_HANDLE.topRight,
            SURFACE_EDITOR_DRAG_HANDLE.bottomLeft,
            SURFACE_EDITOR_DRAG_HANDLE.bottomRight
          ].includes(dragHandle);

          if (affectsWidth) {
            snapDx *= 2;
          }
        }
      }

      // Y-axis: find all edges within threshold and track closest for snapping
      // Prioritize middle alignments over edge alignments
      let minYDistance = SNAP_THRESHOLD + 1;
      let bestYEdge = null;
      let bestYRef = null;

      yEdgesToCheck.forEach(edgeName => {
        const edgeValue = draggedEdges[edgeName];
        yRefLines.forEach(refLine => {
          const distance = Math.abs(edgeValue - refLine.value);

          // Track if this is the closest for snapping
          // Prioritize middle edge: if middle is within threshold, prefer it over edge alignments
          const isMiddle = edgeName === 'middleY';
          const isBetterMatch = distance < minYDistance || (isMiddle && distance <= SNAP_THRESHOLD && !bestYEdge?.includes('middle'));

          if (isBetterMatch) {
            minYDistance = distance;
            bestYEdge = edgeName;
            bestYRef = refLine;
          }

          // Track all alignments within threshold
          if (distance <= SNAP_THRESHOLD) {
            const key = refLine.value;
            if (!allAlignedYPositions.has(key)) {
              allAlignedYPositions.set(key, {edges: new Set(), refLines: []});
            }
            const alignment = allAlignedYPositions.get(key);
            alignment.edges.add(edgeName);
            if (!alignment.refLines.some(r => r === refLine)) {
              alignment.refLines.push(refLine);
            }
          }
        });
      });

      // Apply snap for the closest edge
      if (minYDistance <= SNAP_THRESHOLD && bestYRef) {
        snapDy = bestYRef.value - draggedEdges[bestYEdge];

        // During resize operations, middle position changes at half the rate of edge movement
        // Scale the snap delta by 2x to compensate (only for handles that affect height)
        if (!isMove && bestYEdge === 'middleY') {
          const affectsHeight = [
            SURFACE_EDITOR_DRAG_HANDLE.top,
            SURFACE_EDITOR_DRAG_HANDLE.bottom,
            SURFACE_EDITOR_DRAG_HANDLE.topLeft,
            SURFACE_EDITOR_DRAG_HANDLE.topRight,
            SURFACE_EDITOR_DRAG_HANDLE.bottomLeft,
            SURFACE_EDITOR_DRAG_HANDLE.bottomRight
          ].includes(dragHandle);

          if (affectsHeight) {
            snapDy *= 2;
          }
        }
      }

      // Populate guide alignments for ALL aligned positions
      const xAlignments = {};
      const yAlignments = {};

      allAlignedXPositions.forEach((alignment, xValue) => {
        const refLines = alignment.refLines;
        // Include dragged element's perpendicular span in the union
        const allTops = [...refLines.map(line => line.top), draggedBounds.y];
        const allBottoms = [...refLines.map(line => line.bottom), draggedBounds.y + draggedBounds.height];
        const minTop = Math.min(...allTops);
        const maxBottom = Math.max(...allBottoms);
        const isSurface = refLines.some(line => line.isSurface);

        xAlignments[xValue] = {
          top: minTop,
          bottom: surface.height - maxBottom, // CSS offset from bottom
          isSurface
        };
      });

      allAlignedYPositions.forEach((alignment, yValue) => {
        const refLines = alignment.refLines;
        // Include dragged element's perpendicular span in the union
        const allLefts = [...refLines.map(line => line.left), draggedBounds.x];
        const allRights = [...refLines.map(line => line.right), draggedBounds.x + draggedBounds.width];
        const minLeft = Math.min(...allLefts);
        const maxRight = Math.max(...allRights);
        const isSurface = refLines.some(line => line.isSurface);

        yAlignments[yValue] = {
          left: minLeft,
          right: surface.width - maxRight, // CSS offset from right
          isSurface
        };
      });

      // Update the guide state
      guidesMap[surface.id].xAlignments.value = xAlignments;
      guidesMap[surface.id].yAlignments.value = yAlignments;

      return {snapDx, snapDy};
    },
    clear() {
      guidesMap[surface.id].xAlignments.value = {};
      guidesMap[surface.id].yAlignments.value = {};
    },
    xAlignments: guides.xAlignments,
    yAlignments: guides.yAlignments
  };
}
